import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  FileText, 
  User, 
  Stethoscope,
  CheckCircle,
  Save,
  Printer,
  Download,
  MessageCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import { IPDQueueEntry, DischargeSummaryTemplate } from '@/types/ipd';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  formatIstTimeHHmm,
  istDateAndTimeToIso,
  toIstDateInputValue,
} from '@/utils/dateUtils';

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string } } };
  if (ax.response?.data?.message?.trim()) return ax.response.data.message.trim();
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return fallback;
}

function resolveFollowUpPayload(formData: {
  followUpDate: string;
  followUpTime: string;
  followUpReason: string;
}) {
  const date = formData.followUpDate.trim();
  const time = formData.followUpTime.trim();
  if (date && !time) {
    throw new Error('Follow-up time (IST) is required when date is set');
  }
  if (!date && time) {
    throw new Error('Follow-up date (IST) is required when time is set');
  }
  return {
    followUpAt: date && time ? istDateAndTimeToIso(date, time) : null,
    followUpReason: formData.followUpReason.trim() || null,
  };
}

interface ReceptionistDischargeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  queueEntry: IPDQueueEntry | null;
  /** When OFFLINE, outstanding check uses the parallel settlement bill. */
  billTrack?: 'REVENUE' | 'OFFLINE';
}

export default function ReceptionistDischargeForm({ 
  isOpen, 
  onClose, 
  onSuccess, 
  queueEntry,
  billTrack = 'REVENUE',
}: ReceptionistDischargeFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReceptionist = user?.role === 'RECEPTIONIST';
  const billingPath = (admissionId: string) => {
    if (isReceptionist) {
      return billTrack === 'OFFLINE'
        ? `/receptionist/ipd/${admissionId}/offline-billing`
        : `/receptionist/ipd/${admissionId}/billing`;
    }
    return `/doctor/ipd/${admissionId}/billing`;
  };

  type Drug = { id: string; name: string; strength?: string; form?: string; unit?: string };
  type MedRow = {
    drugId: string;
    customName?: string;
    dose: string;
    frequency: string;
    comment: string;
  };
  type DischargeMedsJson = {
    v: 1;
    items: Array<{ drugId: string; drugName?: string; dose?: string; frequency?: string; comment?: string }>;
    notes?: string;
  };

  const [formData, setFormData] = useState({
    dischargeDate: '',
    finalDiagnosis: '',
    treatmentSummary: '',
    proceduresPerformed: '',
    medicationsPrescribed: '',
    followUpDate: '',
    followUpTime: '',
    followUpReason: '',
    followUpInstructions: '',
    remarks: '',
    doctorSignature: '',
    hospitalStamp: ''
  });
  const [medSearch, setMedSearch] = useState('');
  const [medNotes, setMedNotes] = useState('');
  const [medRows, setMedRows] = useState<MedRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState<DischargeSummaryTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const isPostDischargeEdit = queueEntry?.status === 'DISCHARGED';
  const admissionId = queueEntry?.admission?.id;

  const { data: drugs = [] } = useQuery({
    queryKey: ['drugs-list-discharge'],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>('/api/prescription/drugs');
      return res.data?.data ?? [];
    },
    enabled: isOpen,
  });

  const { data: dischargeBlockers } = useQuery({
    queryKey: ['ipd-discharge-blockers', admissionId, billTrack],
    queryFn: async () => {
      const res = await ipdApi.getDischargeBlockers(admissionId!, billTrack);
      return res.data?.data;
    },
    enabled: isOpen && !!admissionId && !isPostDischargeEdit,
  });

  const hasUnsettledPharma = dischargeBlockers?.hasUnsettledPharma === true;
  const unsettledPharmaItems = dischargeBlockers?.unsettledPharmaItems ?? [];
  const hasOutstandingBalance = dischargeBlockers?.hasOutstandingBalance === true;
  const outstandingAmount = dischargeBlockers?.outstandingAmount ?? 0;
  const revenueOutstandingIgnored =
    (dischargeBlockers as { revenueOutstandingIgnored?: boolean })?.revenueOutstandingIgnored === true;
  const revenueOutstandingAmount =
    (dischargeBlockers as { revenueOutstandingAmount?: number | null })?.revenueOutstandingAmount ?? null;
  const dischargeBlocked = hasOutstandingBalance;

  const filteredDrugs = useMemo(() => {
    const q = medSearch.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => d.name.toLowerCase().includes(q) || (d.strength ?? '').toLowerCase().includes(q));
  }, [drugs, medSearch]);

  const parseMedicationsPrescribed = (raw: string | undefined | null): { rows: MedRow[]; notes: string } => {
    const s = (raw ?? '').trim();
    if (!s) return { rows: [], notes: '' };
    try {
      const obj = JSON.parse(s) as DischargeMedsJson;
      if (!obj || obj.v !== 1 || !Array.isArray(obj.items)) throw new Error('not meds json');
      const rows = obj.items
        .filter((x) => x?.drugId || x?.drugName)
        .map((x) => ({
          drugId: String(x.drugId ?? ''),
          customName: x.drugId ? undefined : String(x.drugName ?? ''),
          dose: String(x.dose ?? ''),
          frequency: String(x.frequency ?? ''),
          comment: String(x.comment ?? ''),
        }));
      return { rows, notes: String(obj.notes ?? '') };
    } catch {
      return { rows: [], notes: s }; // legacy plain text
    }
  };

  const buildMedicationsPrescribedJson = (): string => {
    const items = medRows.map((r) => {
      const drug = drugs.find((d) => d.id === r.drugId);
      const resolvedDrugName = r.drugId
        ? (drug ? `${drug.name}${drug.strength ? ` (${drug.strength})` : ''}` : undefined)
        : (r.customName?.trim() || undefined);
      return {
        drugId: r.drugId,
        drugName: resolvedDrugName,
        dose: r.dose?.trim() || undefined,
        frequency: r.frequency?.trim() || undefined,
        comment: r.comment?.trim() || undefined,
      };
    });
    const payload: DischargeMedsJson = { v: 1, items, notes: medNotes.trim() || undefined };
    return JSON.stringify(payload);
  };

  // Fetch templates when modal opens
  useEffect(() => {
    if (isOpen) {
      ipdApi.getDischargeTemplates()
        .then((res) => setTemplates(res.data?.data ?? []))
        .catch(() => setTemplates([]));
      setSelectedTemplateId('');
    }
  }, [isOpen]);

  // Set default discharge date to current date/time
  useEffect(() => {
    if (isOpen && queueEntry) {
      const now = new Date();
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      
      setFormData(prev => ({
        ...prev,
        dischargeDate: prev.dischargeDate || localDateTime
      }));
    }
  }, [isOpen, queueEntry]);

  // Load existing discharge summary (draft or final) when opening, so fields can be edited
  useEffect(() => {
    const loadExistingSummary = async () => {
      if (!isOpen || !queueEntry?.admission) return;
      try {
        const res = await ipdApi.getDischargeSummary(queueEntry.admission.id);
        const summary = res.data?.data;
        if (!summary) return;
        const toLocalInput = (iso: string | Date) => {
          const d = new Date(iso);
          const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
          return local.toISOString().slice(0, 16);
        };
        setFormData({
          dischargeDate: summary.dischargeDate ? toLocalInput(summary.dischargeDate) : formData.dischargeDate,
          finalDiagnosis: summary.finalDiagnosis ?? '',
          treatmentSummary: summary.treatmentSummary ?? '',
          proceduresPerformed: summary.proceduresPerformed ?? '',
          medicationsPrescribed: summary.medicationsPrescribed ?? '',
          followUpDate: summary.followUpAt ? toIstDateInputValue(summary.followUpAt) : '',
          followUpTime: summary.followUpAt ? formatIstTimeHHmm(summary.followUpAt) : '',
          followUpReason: summary.followUpReason ?? '',
          followUpInstructions: summary.followUpInstructions ?? '',
          remarks: summary.remarks ?? '',
          doctorSignature: summary.doctorSignature ?? '',
          hospitalStamp: summary.hospitalStamp ?? ''
        });
        const parsed = parseMedicationsPrescribed(summary.medicationsPrescribed);
        setMedRows(parsed.rows);
        setMedNotes(parsed.notes);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          // No existing summary yet – that's fine
          return;
        }
        console.error('Failed to load discharge summary', err);
      }
    };
    loadExistingSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, queueEntry?.admission?.id]);

  const handleTemplateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (t) {
      setFormData(prev => ({
        ...prev,
        finalDiagnosis: t.finalDiagnosis ?? '',
        treatmentSummary: t.treatmentSummary ?? '',
        proceduresPerformed: t.proceduresPerformed ?? '',
        medicationsPrescribed: t.medicationsPrescribed ?? '',
        followUpReason: t.followUpReason ?? '',
        followUpInstructions: t.followUpInstructions ?? '',
        remarks: t.remarks ?? '',
        doctorSignature: t.doctorSignature ?? '',
        hospitalStamp: t.hospitalStamp ?? ''
      }));
      const parsed = parseMedicationsPrescribed(t.medicationsPrescribed);
      setMedRows(parsed.rows);
      setMedNotes(parsed.notes);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    const medsPayload = medRows.length > 0 || medNotes.trim() ? buildMedicationsPrescribedJson() : formData.medicationsPrescribed;
    if (!formData.finalDiagnosis || !formData.treatmentSummary || !medsPayload || !formData.followUpInstructions) {
      toast.error('Fill required fields before saving as template');
      return;
    }
    try {
      await ipdApi.createDischargeTemplate({
        name: newTemplateName.trim(),
        finalDiagnosis: formData.finalDiagnosis,
        treatmentSummary: formData.treatmentSummary,
        proceduresPerformed: formData.proceduresPerformed || undefined,
        medicationsPrescribed: medsPayload,
        followUpInstructions: formData.followUpInstructions,
        followUpReason: formData.followUpReason.trim() || null,
        remarks: formData.remarks || undefined,
        doctorSignature: formData.doctorSignature || undefined,
        hospitalStamp: formData.hospitalStamp || undefined
      });
      toast.success('Template saved');
      setSaveTemplateOpen(false);
      setNewTemplateName('');
      const res = await ipdApi.getDischargeTemplates();
      setTemplates(res.data?.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to save template');
    }
  };

  const handleSaveUpdatedTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error('Select a template to update');
      return;
    }
    const medsPayload = medRows.length > 0 || medNotes.trim() ? buildMedicationsPrescribedJson() : formData.medicationsPrescribed;
    if (!formData.finalDiagnosis || !formData.treatmentSummary || !medsPayload || !formData.followUpInstructions) {
      toast.error('Fill required fields before updating template');
      return;
    }
    try {
      await ipdApi.updateDischargeTemplate(selectedTemplateId, {
        finalDiagnosis: formData.finalDiagnosis,
        treatmentSummary: formData.treatmentSummary,
        proceduresPerformed: formData.proceduresPerformed || undefined,
        medicationsPrescribed: medsPayload,
        followUpInstructions: formData.followUpInstructions,
        followUpReason: formData.followUpReason.trim() || null,
        remarks: formData.remarks || undefined,
        doctorSignature: formData.doctorSignature || undefined,
        hospitalStamp: formData.hospitalStamp || undefined
      });
      toast.success('Template updated');
      const res = await ipdApi.getDischargeTemplates();
      setTemplates(res.data?.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update template');
    }
  };

  const handlePrint = async () => {
    if (!queueEntry?.admission) {
      toast.error('No admission data');
      return;
    }
    const medsPayload = medRows.length > 0 || medNotes.trim() ? buildMedicationsPrescribedJson() : formData.medicationsPrescribed;
    if (!formData.dischargeDate || !formData.finalDiagnosis || !formData.treatmentSummary || !medsPayload || !formData.followUpInstructions) {
      toast.error('Please fill all required fields before printing');
      return;
    }
    try {
      setIsPrinting(true);
      const followUp = resolveFollowUpPayload(formData);
      const dischargeDateISO = new Date(formData.dischargeDate).toISOString();
      const res = await ipdApi.generateDischargeSummaryPDF(
        queueEntry.admission.id,
        {
          dischargeDate: dischargeDateISO,
          finalDiagnosis: formData.finalDiagnosis,
          treatmentSummary: formData.treatmentSummary,
          proceduresPerformed: formData.proceduresPerformed || undefined,
          medicationsPrescribed: medsPayload,
          followUpInstructions: formData.followUpInstructions,
          followUpAt: followUp.followUpAt,
          followUpReason: followUp.followUpReason,
          remarks: formData.remarks || undefined,
          doctorSignature: formData.doctorSignature || undefined,
          hospitalStamp: formData.hospitalStamp || undefined
        },
        { forPrint: true }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) w.onload = () => URL.revokeObjectURL(url);
      toast.success('Opening discharge summary for print');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to generate PDF');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDownload = async () => {
    if (!queueEntry?.admission) {
      toast.error('No admission data');
      return;
    }
    const medsPayload = medRows.length > 0 || medNotes.trim() ? buildMedicationsPrescribedJson() : formData.medicationsPrescribed;
    if (!formData.dischargeDate || !formData.finalDiagnosis || !formData.treatmentSummary || !medsPayload || !formData.followUpInstructions) {
      toast.error('Please fill all required fields before downloading');
      return;
    }
    try {
      setIsDownloading(true);
      const followUp = resolveFollowUpPayload(formData);
      const dischargeDateISO = new Date(formData.dischargeDate).toISOString();
      const res = await ipdApi.generateDischargeSummaryPDF(
        queueEntry.admission.id,
        {
          dischargeDate: dischargeDateISO,
          finalDiagnosis: formData.finalDiagnosis,
          treatmentSummary: formData.treatmentSummary,
          proceduresPerformed: formData.proceduresPerformed || undefined,
          medicationsPrescribed: medsPayload,
          followUpInstructions: formData.followUpInstructions,
          followUpAt: followUp.followUpAt,
          followUpReason: followUp.followUpReason,
          remarks: formData.remarks || undefined,
          doctorSignature: formData.doctorSignature || undefined,
          hospitalStamp: formData.hospitalStamp || undefined
        }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Discharge-Summary-${queueEntry.ipdNumber ?? queueEntry.admission.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Discharge summary downloaded');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to download PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!queueEntry?.admission) {
      toast.error('No admission data');
      return;
    }
    if (!queueEntry.patient?.phone) {
      toast.error('Patient has no registered phone number');
      return;
    }
    const medsPayload = medRows.length > 0 || medNotes.trim() ? buildMedicationsPrescribedJson() : formData.medicationsPrescribed;
    if (!formData.dischargeDate || !formData.finalDiagnosis || !formData.treatmentSummary || !medsPayload || !formData.followUpInstructions) {
      toast.error('Please fill all required fields before sending');
      return;
    }
    try {
      setIsSendingWhatsApp(true);
      const followUp = resolveFollowUpPayload(formData);
      const dischargeDateISO = new Date(formData.dischargeDate).toISOString();
      await ipdApi.sendDischargeSummaryWhatsApp(queueEntry.admission.id, {
        dischargeDate: dischargeDateISO,
        finalDiagnosis: formData.finalDiagnosis,
        treatmentSummary: formData.treatmentSummary,
        proceduresPerformed: formData.proceduresPerformed || undefined,
        medicationsPrescribed: medsPayload,
        followUpInstructions: formData.followUpInstructions,
        followUpAt: followUp.followUpAt,
        followUpReason: followUp.followUpReason,
        doctorSignature: formData.doctorSignature || undefined,
        hospitalStamp: formData.hospitalStamp || undefined
      });
      toast.success('Discharge summary sent via WhatsApp');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to send via WhatsApp');
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!queueEntry || !queueEntry.admission) {
      toast.error('No admission data available');
      return;
    }

    const medsPayload = medRows.length > 0 || medNotes.trim() ? buildMedicationsPrescribedJson() : formData.medicationsPrescribed;
    // Validation
    if (!formData.dischargeDate || !formData.finalDiagnosis || !formData.treatmentSummary || 
        !medsPayload || !formData.followUpInstructions) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isPostDischargeEdit && hasUnsettledPharma && hasOutstandingBalance) {
      toast.error(
        'Cannot discharge: IPD pharmacy medicines are pending collection. Mark them on the IPD bill or settle at the pharmacy.'
      );
      return;
    }
    if (!isPostDischargeEdit && hasOutstandingBalance) {
      toast.error(
        `Cannot discharge: ${billTrack === 'OFFLINE' ? 'offline billing' : 'IPD bill'} outstanding balance is ₹${outstandingAmount.toFixed(2)}. Record deposits until balance is zero.`
      );
      return;
    }

    try {
      setIsSubmitting(true);
      const followUp = resolveFollowUpPayload(formData);
      const dischargeDateISO = new Date(formData.dischargeDate).toISOString();

      // Create discharge summary
      await ipdApi.createDischargeSummary({
        admissionId: queueEntry.admission.id,
        dischargeDate: dischargeDateISO,
        finalDiagnosis: formData.finalDiagnosis,
        treatmentSummary: formData.treatmentSummary,
        proceduresPerformed: formData.proceduresPerformed,
        medicationsPrescribed: medsPayload,
        followUpInstructions: formData.followUpInstructions,
        followUpAt: followUp.followUpAt,
        followUpReason: followUp.followUpReason,
        remarks: formData.remarks || undefined,
        doctorSignature: formData.doctorSignature,
        hospitalStamp: formData.hospitalStamp,
        billTrack,
      });

      toast.success(isPostDischargeEdit ? 'Discharge summary updated' : 'Patient discharged successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error discharging patient:', error);
      toast.error(
        apiErrorMessage(
          error,
          isPostDischargeEdit ? 'Failed to update discharge summary' : 'Failed to discharge patient'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!queueEntry || !queueEntry.admission) {
      toast.error('No admission data available');
      return;
    }
    try {
      setIsSavingDraft(true);
      const followUp = resolveFollowUpPayload(formData);
      const medsPayload = medRows.length > 0 || medNotes.trim() ? buildMedicationsPrescribedJson() : formData.medicationsPrescribed;
      const dischargeDateISO = formData.dischargeDate ? new Date(formData.dischargeDate).toISOString() : new Date().toISOString();
      await ipdApi.createDischargeSummary({
        admissionId: queueEntry.admission.id,
        dischargeDate: dischargeDateISO,
        finalDiagnosis: formData.finalDiagnosis,
        treatmentSummary: formData.treatmentSummary,
        proceduresPerformed: formData.proceduresPerformed || undefined,
        medicationsPrescribed: medsPayload,
        followUpInstructions: formData.followUpInstructions,
        followUpAt: followUp.followUpAt,
        followUpReason: followUp.followUpReason,
        remarks: formData.remarks || undefined,
        doctorSignature: formData.doctorSignature || undefined,
        hospitalStamp: formData.hospitalStamp || undefined,
        isDraft: true,
      });
      toast.success('Discharge summary draft saved');
    } catch (error) {
      console.error('Error saving draft discharge summary:', error);
      toast.error(apiErrorMessage(error, 'Failed to save draft'));
    } finally {
      setIsSavingDraft(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPatientAge = (dob?: string) => {
    if (!dob) return '—';
    const birthDate = new Date(dob);
    const now = new Date();
    let years = now.getFullYear() - birthDate.getFullYear();
    const monthDiff = now.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
      years -= 1;
    }
    return years >= 0 ? `${years} years` : '—';
  };

  const calculateStayDuration = () => {
    if (!queueEntry?.admission || !formData.dischargeDate) return 0;
    const admissionDate = new Date(queueEntry.admission.admissionDate);
    const dischargeDate = new Date(formData.dischargeDate);
    const diffTime = Math.abs(dischargeDate.getTime() - admissionDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (!queueEntry || !queueEntry.admission) return null;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-red-600" />
            Discharge Patient
          </DialogTitle>
          <DialogDescription>
            {queueEntry.admission?.useOfflineBilling
              ? 'Complete the discharge summary. For insurance/offline cases, only the offline (OIPD) bill must be settled — main revenue bill balance is not required. Pharmacy lines must still be collected.'
              : 'Complete the discharge summary. Discharge requires zero IPD bill balance (pharmacy lines on the bill are settled when the balance is cleared).'}
          </DialogDescription>
        </DialogHeader>

        {!isPostDischargeEdit && revenueOutstandingIgnored && !hasOutstandingBalance && (
          <div
            className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950 mb-3"
            role="status"
          >
            Insurance/offline workflow: main revenue bill may still show
            {revenueOutstandingAmount != null && revenueOutstandingAmount > 0.009
              ? ` ₹${revenueOutstandingAmount.toFixed(2)} outstanding`
              : ' a balance'}
            — discharge is allowed once offline billing is settled.
          </div>
        )}

        {!isPostDischargeEdit && hasOutstandingBalance && (
          <div
            className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-950 mb-3"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium">
                  {billTrack === 'OFFLINE' ? 'Offline billing balance due' : 'IPD bill balance due'} — discharge blocked
                </p>
                <p className="mt-1">
                  Outstanding: <strong>₹{outstandingAmount.toFixed(2)}</strong>. Record payment on the Deposits tab
                  before final discharge.
                </p>
                {admissionId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3 border-red-400 bg-white hover:bg-red-100"
                    onClick={() => {
                      onClose();
                      navigate(billingPath(admissionId));
                    }}
                  >
                    Open IPD billing
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {!isPostDischargeEdit && hasUnsettledPharma && hasOutstandingBalance && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium">Pharmacy billing pending — discharge blocked</p>
                <p className="mt-1 text-amber-900">
                  {unsettledPharmaItems.length} medicine line{unsettledPharmaItems.length === 1 ? '' : 's'} must be
                  collected at the pharmacy counter or settled on the IPD bill before you can discharge this patient.
                </p>
                {unsettledPharmaItems.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 space-y-0.5 text-amber-900">
                    {unsettledPharmaItems.slice(0, 5).map((item) => (
                      <li key={item.id}>{item.description}</li>
                    ))}
                    {unsettledPharmaItems.length > 5 && (
                      <li>…and {unsettledPharmaItems.length - 5} more</li>
                    )}
                  </ul>
                )}
                {admissionId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3 border-amber-400 bg-white hover:bg-amber-100"
                    onClick={() => {
                      onClose();
                      navigate(billingPath(admissionId));
                    }}
                  >
                    Open IPD billing
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Patient Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Patient Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Patient Name</div>
                  <div className="font-medium">{queueEntry.patient.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Age: {getPatientAge(queueEntry.patient.dob)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">IPD Number</div>
                  <div className="font-medium">{queueEntry.ipdNumber}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Ward</div>
                  <Badge className="bg-blue-100 text-blue-800">
                    {queueEntry.admission.wardType}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Room/Bed</div>
                  <div className="font-medium">
                    {queueEntry.admission.roomNumber || 'N/A'} / {queueEntry.admission.bedNumber || 'N/A'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="text-sm text-gray-500">Admission Date</div>
                  <div className="font-medium">{formatDate(queueEntry.admission.admissionDate)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Stay Duration</div>
                  <div className="font-medium">{calculateStayDuration()} days</div>
                </div>
              </div>
              {queueEntry.admission?.surgeryName && (
                <div className="mt-4">
                  <div className="text-sm text-gray-500">Procedure / Surgery</div>
                  <div className="font-medium">{queueEntry.admission.surgeryName}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Discharge Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Discharge Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="templateSelect">Load template</Label>
                <select
                  id="templateSelect"
                  value={selectedTemplateId}
                  onChange={handleTemplateSelect}
                  className="mt-1 flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">— Select a template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Selecting a template fills the fields below; you can edit them.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dischargeDate" className="text-red-600">
                    Discharge Date & Time *
                  </Label>
                  <Input
                    id="dischargeDate"
                    name="dischargeDate"
                    type="datetime-local"
                    value={formData.dischargeDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="finalDiagnosis" className="text-red-600">
                    Final Diagnosis *
                  </Label>
                  <Input
                    id="finalDiagnosis"
                    name="finalDiagnosis"
                    value={formData.finalDiagnosis}
                    onChange={handleInputChange}
                    placeholder="Enter final diagnosis"
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="treatmentSummary" className="text-red-600">
                  Treatment Summary *
                </Label>
                <Textarea
                  id="treatmentSummary"
                  name="treatmentSummary"
                  value={formData.treatmentSummary}
                  onChange={handleInputChange}
                  placeholder="Summarize the treatment provided during the stay"
                  required
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="proceduresPerformed">
                  Procedures Performed
                </Label>
                <Textarea
                  id="proceduresPerformed"
                  name="proceduresPerformed"
                  value={formData.proceduresPerformed}
                  onChange={handleInputChange}
                  placeholder="List any procedures or surgeries performed"
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="medicationsPrescribed" className="text-red-600">
                  Medications Prescribed *
                </Label>
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search in-house medicine to add..."
                      value={medSearch}
                      onChange={(e) => setMedSearch(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setMedRows((prev) => [
                          ...prev,
                          { drugId: '', customName: '', dose: '', frequency: '', comment: '' },
                        ])
                      }
                    >
                      + Custom medicine
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto border rounded-md p-2 bg-gray-50">
                    {filteredDrugs.slice(0, 30).map((d) => (
                      <Button
                        key={d.id}
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setMedRows((prev) =>
                            prev.some((x) => x.drugId === d.id && !x.customName)
                              ? prev
                              : [...prev, { drugId: d.id, customName: '', dose: '', frequency: '', comment: '' }]
                          )
                        }
                      >
                        + {d.name} {d.strength ? `(${d.strength})` : ''}
                      </Button>
                    ))}
                  </div>

                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-2">Medicine</th>
                          <th className="text-left p-2">Dose</th>
                          <th className="text-left p-2">Frequency</th>
                          <th className="text-left p-2">Comment</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {medRows.length === 0 ? (
                          <tr><td colSpan={5} className="p-3 text-gray-500">No medicines added</td></tr>
                        ) : (
                          medRows.map((r, idx) => {
                            const drug = drugs.find((dd) => dd.id === r.drugId);
                            const isCustom = !r.drugId;
                            return (
                              <tr key={`${r.drugId || 'custom'}-${idx}`} className="border-t">
                                <td className="p-2">
                                  {isCustom ? (
                                    <Input
                                      value={r.customName || ''}
                                      onChange={(e) =>
                                        setMedRows((p) =>
                                          p.map((x, i) => (i === idx ? { ...x, customName: e.target.value } : x))
                                        )
                                      }
                                      placeholder="Enter custom medicine name"
                                    />
                                  ) : (
                                    <span>
                                      {drug?.name} {drug?.strength ? `(${drug.strength})` : ''}
                                    </span>
                                  )}
                                </td>
                                <td className="p-2">
                                  <Input
                                    value={r.dose}
                                    onChange={(e) => setMedRows((p) => p.map((x, i) => (i === idx ? { ...x, dose: e.target.value } : x)))}
                                    placeholder="e.g. 1 tab"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    value={r.frequency}
                                    onChange={(e) => setMedRows((p) => p.map((x, i) => (i === idx ? { ...x, frequency: e.target.value } : x)))}
                                    placeholder="e.g. BD / TDS"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    value={r.comment}
                                    onChange={(e) => setMedRows((p) => p.map((x, i) => (i === idx ? { ...x, comment: e.target.value } : x)))}
                                    placeholder="Optional"
                                  />
                                </td>
                                <td className="p-2 text-right">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setMedRows((p) => p.filter((_, i) => i !== idx))}
                                  >
                                    Remove
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <Textarea
                    value={medNotes}
                    onChange={(e) => setMedNotes(e.target.value)}
                    placeholder="Extra notes (optional). If you had an old free-text list, paste it here."
                    rows={2}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="followUpDate">Follow-up date (IST)</Label>
                  <Input
                    id="followUpDate"
                    name="followUpDate"
                    type="date"
                    value={formData.followUpDate}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="followUpTime">Follow-up time (IST)</Label>
                  <Input
                    id="followUpTime"
                    name="followUpTime"
                    type="time"
                    value={formData.followUpTime}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                Follow-up date and time are stored in Indian Standard Time (IST).
              </p>

              <div>
                <Label htmlFor="followUpReason">Reason for follow-up</Label>
                <Textarea
                  id="followUpReason"
                  name="followUpReason"
                  value={formData.followUpReason}
                  onChange={handleInputChange}
                  placeholder="e.g. Suture removal, wound check, review reports"
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="followUpInstructions" className="text-red-600">
                  Follow-up Instructions *
                </Label>
                <Textarea
                  id="followUpInstructions"
                  name="followUpInstructions"
                  value={formData.followUpInstructions}
                  onChange={handleInputChange}
                  placeholder="Provide follow-up care instructions"
                  required
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="remarks">
                  Remarks
                </Label>
                <Textarea
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  placeholder="Any additional remarks for discharge summary"
                  rows={2}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Doctor Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Doctor Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="doctorSignature">
                    Doctor Signature
                  </Label>
                  <Input
                    id="doctorSignature"
                    name="doctorSignature"
                    value={formData.doctorSignature}
                    onChange={handleInputChange}
                    placeholder="Doctor's signature"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="hospitalStamp">
                    Hospital Stamp
                  </Label>
                  <Input
                    id="hospitalStamp"
                    name="hospitalStamp"
                    value={formData.hospitalStamp}
                    onChange={handleInputChange}
                    placeholder="Hospital stamp/approval"
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSaveTemplateOpen(true)}
                disabled={isSubmitting}
              >
                <Save className="h-4 w-4 mr-1" />
                Save as template
              </Button>
              {selectedTemplateId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSaveUpdatedTemplate}
                  disabled={isSubmitting}
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save updated template
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={isSubmitting || isPrinting}
              >
                {isPrinting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                ) : (
                  <Printer className="h-4 w-4 mr-1" />
                )}
                Print
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={isSubmitting || isDownloading}
              >
                {isDownloading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                ) : (
                  <Download className="h-4 w-4 mr-1" />
                )}
                Download
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSendWhatsApp}
                disabled={isSubmitting || isSendingWhatsApp}
              >
                {isSendingWhatsApp ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                ) : (
                  <MessageCircle className="h-4 w-4 mr-1" />
                )}
                Send via WhatsApp
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={isSubmitting || isSavingDraft}
              >
                {isSavingDraft ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                    Saving draft...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save as draft
                  </div>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={isSubmitting || (!isPostDischargeEdit && dischargeBlocked)}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Discharging...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Discharge Patient
                  </div>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    {/* Save as template dialog (separate so it is not nested) */}
    <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="templateName">Template name</Label>
            <Input
              id="templateName"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="e.g. General Ward Discharge"
              className="mt-1"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setSaveTemplateOpen(false); setNewTemplateName(''); }}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveAsTemplate}>
              Save template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
}
